/**
 * Um diálogo só para criar e para renomear.
 *
 * São o mesmo formulário (um campo, o mesmo schema, a mesma mensagem de erro)
 * e o que muda é o verbo. Duplicar o componente só duplicaria a chance de as
 * duas validações saírem do lugar uma da outra.
 */
import { useEffect } from 'react';
import { Button, Dialog, Field, Input } from '@mgdlead/ui';

import { FormAlert } from '@mgdlead/ui';
import { monitorNameSchema } from '../../../lib/schemas';
import type { MonitorNameValues } from '../../../lib/schemas';
import { useZodForm } from '../../../lib/useZodForm';

export interface MonitorNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'rename';
  /** Nome atual, quando renomeia. */
  initialName?: string;
  onSubmit: (name: string) => Promise<void>;
}

const HINT = {
  create:
    'Use algo que identifique o aparelho, não o grupo: “Chip ofertas 1”, “iPhone da Ana”. Um monitorador pode monitorar vários grupos.',
  rename: 'O nome só serve para você reconhecer o número aqui dentro.',
} as const;

export function MonitorNameDialog({
  open,
  onOpenChange,
  mode,
  initialName = '',
  onSubmit,
}: MonitorNameDialogProps) {
  const form = useZodForm({
    schema: monitorNameSchema,
    initialValues: { name: initialName } satisfies MonitorNameValues,
    onSubmit: async (values) => {
      await onSubmit(values.name);
      onOpenChange(false);
    },
  });

  const { reset } = form;
  // Reabrir com o texto da vez passada renomearia o monitorador errado.
  useEffect(() => {
    if (open) reset({ name: initialName });
  }, [open, initialName, reset]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'Adicionar monitorador' : 'Renomear monitorador'}
      description={
        mode === 'create' ? 'O número só é conectado no passo seguinte, pelo QR code.' : undefined
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={form.pending}>
            Cancelar
          </Button>
          {/*
            O botão vive no rodapé do diálogo, fora do <form>. `form={FORM_ID}`
            é o que o liga de volta — sem isso ele vira um botão decorativo e o
            Enter no campo passa a ser o único jeito de salvar.
          */}
          <Button
            variant="primary"
            loading={form.pending}
            loadingLabel="Salvando"
            form={FORM_ID}
            type="submit"
          >
            {mode === 'create' ? 'Criar e conectar' : 'Salvar nome'}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-3">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="Nome do monitorador" hint={HINT[mode]} error={form.errors.name} required>
          {(control) => (
            <Input
              {...control}
              name="name"
              autoComplete="off"
              placeholder="Chip ofertas 1"
              value={form.values.name}
              invalid={form.errors.name !== undefined}
              onChange={(event) => form.setField('name', event.target.value)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

const FORM_ID = 'monitor-name-form';
